import { useState, useEffect, useMemo } from "react";
import CustomListContainer from "../../../core/components/core/Util/List/CustomList/CustomListContainer";
import CustomForm from "../../../core/components/core/Util/Form/CustomForm/CustomForm";
import Fetch from "../../../core/util/core/Fetch";
import config from "../../config/config";
import { getYears } from "../Files/Util/helper";
import "./FilesMonitoring.css";

const SPHERE_OPTIONS = [
  { label: "Todas", value: "" },
  { label: "Municipal", value: "Municipal" },
  { label: "Estadual", value: "Estadual" },
  { label: "Nacional", value: "Nacional" },
];

const PERIODICITY_OPTIONS = [
  { label: "Todas", value: "" },
  { label: "Mensal", value: "Mensal" },
  { label: "Anual", value: "Anual" },
  { label: "Semestral", value: "Semestral" },
  { label: "Quadrimestral", value: "Quadrimestral" },
  { label: "Arquivo único", value: "Arquivo único" },
];

const CELL = {
  DONE: { icon: "fas fa-check-square", className: "fm-cell-done", title: "Enviado e processado" },
  ERROR: { icon: "fas fa-window-close", className: "fm-cell-error", title: "Enviado com erro" },
};
const CELL_PENDING = { icon: "far fa-square", className: "fm-cell-pending", title: "Não enviado" };
const CELL_PROCESSING = { icon: "fas fa-spinner", className: "fm-cell-processing", title: "Em processamento" };

const cellOf = (value) => {
  if (!value) return CELL_PENDING;
  return CELL[value.status] || CELL_PROCESSING;
};

const yearOptions = getYears({ start: 2000 }).map((y) => ({ label: String(y), value: String(y) }));

function FilesMonitoring() {
  const endpoints = useMemo(
    () => ({
      matrix: `${config.endpoints.backend}governanceroom/filesmonitoring/getmatrix`,
      fileTypes: `${config.endpoints.backend}governanceroom/filetypes/getallhelps`,
      states: `${config.endpoints.backend}governanceroom/states/getall`,
    }),
    [],
  );

  const currentYear = new Date().getFullYear();
  const [fileTypes, setFileTypes] = useState([]);
  const [data, setData] = useState({ columns: [], rows: [], fileType: null });
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    sphere: "",
    periodicity: "",
    governance_room_file_type_id: "",
    state_uf: "",
    year_start: String(currentYear - 10),
    year_end: String(currentYear),
  });

  useEffect(() => {
    Fetch.post(endpoints.fileTypes, {}).then((rows) => setFileTypes(rows || []));
  }, [endpoints]);

  useEffect(() => {
    if (!values.governance_room_file_type_id) {
      setData({ columns: [], rows: [], fileType: null });
      return;
    }
    setLoading(true);
    Fetch.post(endpoints.matrix, { filter: values })
      .then((response) => setData({
        columns: response?.columns || [],
        rows: response?.rows || [],
        fileType: response?.fileType || null,
      }))
      .finally(() => setLoading(false));
  }, [endpoints, values]);

  const selectedType = fileTypes.find((t) => t.id === values.governance_room_file_type_id);
  const showStateFilter = selectedType && (selectedType.sphere === "Estadual" || selectedType.sphere === "Municipal");

  const handleChange = (element) => {
    const { name, value } = element.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
      // trocar esfera/periodicidade invalida o tipo escolhido
      ...(name === "sphere" || name === "periodicity"
        ? { governance_room_file_type_id: "", state_uf: "" }
        : {}),
    }));
  };

  const form = useMemo(() => {
    const typeOptions = fileTypes
      .filter((t) => !values.sphere || t.sphere === values.sphere)
      .filter((t) => !values.periodicity || t.periodicity === values.periodicity)
      .map((t) => ({ label: t.file_type, value: t.id }));

    const fields = [
      { name: "sphere", type: "select", label: "Esfera", options: SPHERE_OPTIONS, handleChange, validations: { required: false }, columnSize: 2 },
      { name: "periodicity", type: "select", label: "Periodicidade", options: PERIODICITY_OPTIONS, handleChange, validations: { required: false }, columnSize: 2 },
      {
        name: "governance_room_file_type_id",
        type: "select",
        label: "Tipo de Arquivo *",
        options: typeOptions,
        handleChange,
        validations: { required: false },
        columnSize: 4,
      },
      { name: "year_start", type: "select", label: "De", options: yearOptions, handleChange, validations: { required: false }, columnSize: 1 },
      { name: "year_end", type: "select", label: "Até", options: yearOptions, handleChange, validations: { required: false }, columnSize: 1 },
    ];

    if (showStateFilter) {
      fields.push({
        name: "state_uf",
        type: "select",
        label: "Estado (UF)",
        options: [],
        optionsEndpoint: {
          endpoint: endpoints.states,
          label: "geografic_state_uf",
          value: "geografic_state_uf",
        },
        handleChange,
        validations: { required: false },
        columnSize: 2,
      });
    }

    return {
      validateOnChange: true,
      showGoBackButton: false,
      showSubmitButton: false,
      update_key: `${values.sphere}-${values.periodicity}-${showStateFilter}`,
      handleSubmit: () => {},
      fields,
      values,
    };
  }, [fileTypes, values, showStateFilter, endpoints]);

  const showUf = data.fileType && (data.fileType.sphere === "Estadual" || data.fileType.sphere === "Municipal");
  const showCity = data.fileType && data.fileType.sphere === "Municipal";

  // Ano vira célula agrupada: só a primeira linha do ano o imprime, com rowspan.
  const rowsWithSpan = useMemo(() => {
    const spans = {};
    data.rows.forEach((row) => {
      spans[row.ano] = (spans[row.ano] || 0) + 1;
    });
    let lastYear = null;
    return data.rows.map((row) => {
      const first = row.ano !== lastYear;
      lastYear = row.ano;
      return { ...row, yearSpan: first ? spans[row.ano] : 0 };
    });
  }, [data.rows]);

  const totals = useMemo(() => {
    const expected = data.rows.length * data.columns.length;
    let sent = 0;
    data.rows.forEach((row) => data.columns.forEach((c) => {
      if (row[c.key] && row[c.key].status === "DONE") sent++;
    }));
    return { sent, expected };
  }, [data]);

  const content = (
    <div className="card-body fm-body">
      <CustomForm form={form} key={form.update_key} />

      {!values.governance_room_file_type_id && (
        <div className="fm-empty">
          <i className="fas fa-table" />
          <span>Selecione um tipo de arquivo para ver o acompanhamento.</span>
        </div>
      )}

      {loading && (
        <div className="fm-empty"><i className="fas fa-spinner fa-spin" /><span>Carregando...</span></div>
      )}

      {!loading && !!values.governance_room_file_type_id && !data.rows.length && (
        <div className="fm-empty">
          <i className="fas fa-inbox" />
          <span>Nenhum arquivo enviado para este tipo no período selecionado.</span>
        </div>
      )}

      {!loading && !!data.rows.length && (
        <>
          <div className="fm-toolbar">
            <div className="fm-summary">
              <strong>{totals.sent}</strong> de {totals.expected} períodos enviados
              <div className="fm-progress">
                <div className="fm-progress-bar" style={{ width: `${(totals.sent / totals.expected) * 100}%` }} />
              </div>
            </div>
            <div className="fm-legend">
              <span><i className={`${CELL.DONE.icon} ${CELL.DONE.className}`} />Enviado</span>
              <span><i className={`${CELL_PENDING.icon} ${CELL_PENDING.className}`} />Não enviado</span>
              <span><i className={`${CELL.ERROR.icon} ${CELL.ERROR.className}`} />Com erro</span>
              <span><i className={`${CELL_PROCESSING.icon} ${CELL_PROCESSING.className}`} />Processando</span>
            </div>
          </div>

          <div className="fm-table-wrapper">
            <table className="fm-table">
              <thead>
                <tr>
                  <th className="fm-sticky-col fm-col-year">Ano</th>
                  {showUf && <th className="fm-col-uf">UF</th>}
                  {showCity && <th className="fm-col-city">Município</th>}
                  {data.columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th className="fm-col-count">Enviados</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithSpan.map((row) => {
                  const sent = data.columns.filter((c) => row[c.key] && row[c.key].status === "DONE").length;
                  return (
                    <tr key={`${row.ano}-${row.uf || ""}-${row.city_id || ""}`}>
                      {!!row.yearSpan && (
                        <td className="fm-sticky-col fm-year" rowSpan={row.yearSpan}>{row.ano}</td>
                      )}
                      {showUf && <td className="fm-scope">{row.uf || "-"}</td>}
                      {showCity && <td className="fm-scope fm-scope-city">{row.city_name || "-"}</td>}
                      {data.columns.map((column) => {
                        const value = row[column.key];
                        const cell = cellOf(value);
                        return (
                          <td key={column.key} className="fm-cell">
                            <i
                              className={`${cell.icon} ${cell.className}`}
                              title={value?.created_at
                                ? `${cell.title} em ${new Date(value.created_at).toLocaleDateString("pt-BR")}`
                                : cell.title}
                            />
                          </td>
                        );
                      })}
                      <td className={`fm-count ${sent === data.columns.length ? "fm-count-full" : ""}`}>
                        {sent}/{data.columns.length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  return (
    <CustomListContainer
      canAdd={false}
      labelTitle="Acompanhamento de Arquivos Enviados"
      labelTitleButtonAdd=""
      content={content}
    />
  );
}

export default FilesMonitoring;
